import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShareIcon from '@mui/icons-material/Share';
import DirectionsIcon from '@mui/icons-material/Directions';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useVenue } from '../hooks/useVenues';
import { getVenueImages, getVenueOffers, PublicOffer, RestoDto, venueImageUrl } from '../api';
import { trackEvent } from '../analytics';
import { useFeatures } from '../features';
import { VenueEventsSection } from '../features/events/pages';
import EmptyState from './EmptyState';
import ShareModal from './ShareModal';
import FitImage from './FitImage';
import VenueOfferHighlights from './VenueOfferHighlights';
import { InviteToHangoutButton } from '../features/hangouts/components/InviteParts';
import '../features/hangouts/hangouts.css';
import { PROPOSE_UI_COLORS } from '../theme';

// A venue's public page: everything shown is real (GET /api/discovery/restaurants/{id} or
// /wineshops/{id}, the partner's photos and their live offers and events). Nothing is invented: no
// placeholder ratings, reviews, opening hours or stock photos. Customers browse, call the venue or
// get directions; there is no claiming or booking on Cheers2Night.
const VenueDetailsPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const features = useFeatures();
  const [shareOpen, setShareOpen] = useState(false);
  const [photos, setPhotos] = useState<string[]>([]);
  const [liveOffers, setLiveOffers] = useState<PublicOffer[]>([]);

  const [kind, idStr] = (slug ?? '').split('-');
  const id = Number(idStr);
  const { venue: found, status } = useVenue(kind, id);

  useEffect(() => {
    setPhotos([]);
    setLiveOffers([]);
    if (kind !== 'resto' || !id) return;
    getVenueImages(id)
      .then((images) => setPhotos(images.map((img) => venueImageUrl(img.url)).filter((u): u is string => !!u)))
      .catch(() => setPhotos([]));
    getVenueOffers(id).then(setLiveOffers).catch(() => setLiveOffers([]));
  }, [kind, id]);

  const venue = useMemo(() => {
    if (!found) return null;
    if (kind === 'resto') {
      const r = found as RestoDto;
      return { name: r.name, address: r.address, cover: venueImageUrl(r.filePath), phone: r.mobile1, isShop: false, lat: r.latitude, lng: r.longitude };
    }
    if (kind === 'shop') {
      const s = found;
      return { name: s.name, address: s.address, cover: undefined, phone: s.mobile1, isShop: true, lat: s.latitude, lng: s.longitude };
    }
    return null;
  }, [kind, found]);

  if (status === 'loading') {
    return <Box sx={{ pt: 20, textAlign: 'center' }}><Typography color="text.secondary">Loading venue...</Typography></Box>;
  }

  if (!venue) {
    return (
      <Box sx={{ maxWidth: 640, mx: 'auto', pt: 20, px: 3 }}>
        <EmptyState title="Venue not found" description="It may have moved or is no longer listed." />
        <Box textAlign="center"><Button component={Link} to="/explore" variant="contained" color="primary">Back to Listings</Button></Box>
      </Box>
    );
  }

  const mapQuery = venue.lat && venue.lng ? `${venue.lat},${venue.lng}` : encodeURIComponent(`${venue.name}, ${venue.address}`);
  const directions = `https://www.google.com/maps/dir/?api=1&destination=${mapQuery}`;
  const heroImage = venue.cover ?? photos[0];
  const telLink = venue.phone ? `tel:${venue.phone.replace(/[^\d+]/g, '')}` : undefined;

  return (
    <Box sx={{ maxWidth: '900px', mx: 'auto', px: { xs: 3, md: 6 }, pt: 12, pb: 8 }}>
      <Button component={Link} to="/explore" startIcon={<ArrowBackIcon />} size="small" sx={{ color: 'text.secondary', mb: 2 }}>
        Back to Listings
      </Button>

      {heroImage && (
        <Box sx={{ position: 'relative', borderRadius: 3, overflow: 'hidden', height: 320, mb: 3 }}>
          <FitImage src={heroImage} alt={venue.name} />
        </Box>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} mb={1}>
        <Typography variant="h2" fontSize={32}>{venue.name}</Typography>
        <IconButton onClick={() => setShareOpen(true)} aria-label={`Share ${venue.name}`} sx={{ border: '1px solid rgba(245,246,248,0.15)' }}>
          <ShareIcon sx={{ color: '#fff' }} />
        </IconButton>
      </Box>
      <Box display="flex" alignItems="center" gap={0.5} color="text.secondary" mb={3}>
        <LocationOnIcon sx={{ fontSize: 16 }} /><Typography fontSize={14} color="text.secondary">{venue.address}</Typography>
      </Box>

      <Box display="flex" gap={1.5} flexWrap="wrap" mb={5}>
        {telLink && (
          <Button variant="contained" color="primary" startIcon={<PhoneIcon />} href={telLink} onClick={() => trackEvent('call_click', { venue_id: id })}>
            Call Venue
          </Button>
        )}
        <Button variant="outlined" startIcon={<DirectionsIcon />} href={directions} target="_blank" rel="noreferrer"
          onClick={() => trackEvent('directions_click', { venue_id: id, source: 'venue' })} sx={{ borderColor: 'rgba(245,246,248,0.3)', color: '#fff' }}>
          Get Directions
        </Button>
        <Button variant="outlined" startIcon={<ShareIcon />} onClick={() => setShareOpen(true)} sx={{ borderColor: 'rgba(245,246,248,0.3)', color: '#fff' }}>
          Share
        </Button>
      </Box>

      {kind === 'resto' && (
        <Box mt={-3} mb={4}>
          <InviteToHangoutButton venueId={id} />
          <Typography fontSize={12} color="text.secondary" textAlign="center" mt={1}>Plan a night out here and invite your friends.</Typography>
        </Box>
      )}

      {photos.length > 0 && (
        <Box display="grid" gridTemplateColumns="repeat(4, 1fr)" gap={1.5} mb={5}>
          {photos.slice(0, 8).map((img, i) => (
            <Box key={img} component="a" href={img} target="_blank" rel="noreferrer" aria-label={`Photo ${i + 1} of ${venue.name}`} sx={{ borderRadius: 2, overflow: 'hidden', aspectRatio: '1 / 1', display: 'block' }}>
              <img src={img} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </Box>
          ))}
        </Box>
      )}

      {liveOffers.length > 0
        ? <VenueOfferHighlights offers={liveOffers} venueSlug={slug ?? ''} canClaim={features.offerClaims} />
        : !venue.isShop && (
          <Box mb={5}>
            <Typography variant="h3" fontSize={22} mb={1}>Current Offers</Typography>
            <Typography color="text.secondary">This venue has no offers listed right now. Call them to ask about tonight.</Typography>
          </Box>
        )}

      {kind === 'resto' && <VenueEventsSection restoId={id} />}

      <Typography variant="h3" fontSize={22} mb={1.5}>Location</Typography>
      {/* Keyless Google Maps embed, pinned on the venue's coordinates (or its address when those are unknown),
          darkened to match the site. */}
      <Box sx={{ height: 220, borderRadius: 2, mb: 1.5, overflow: 'hidden', background: PROPOSE_UI_COLORS.surfaceAlt }}>
        <iframe
          title={`Map of ${venue.name}`}
          src={`https://maps.google.com/maps?q=${mapQuery}&z=16&output=embed`}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          style={{ width: '100%', height: '100%', border: 0, filter: 'invert(90%) hue-rotate(180deg) brightness(0.95) contrast(0.9)' }}
        />
      </Box>
      <Typography color="text.secondary" mb={5}>{venue.address}</Typography>

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} eventName={venue.name} />
    </Box>
  );
};

export default VenueDetailsPage;
